import NoDocuments from "Assets/Img/NoDocuments";
import Button from "Components/Common/Button/Button";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useDocuments } from "hooks/useDocuments";

interface DocumentsSectionProps {
  projectId: string;
}

const getIconLabel = (fileName: string) => {
  const ext = (fileName.split(".").pop() ?? "").toLowerCase();
  if (ext === "pdf") return "PDF";
  if (["doc", "docx"].includes(ext)) return "DOC";
  if (["xls", "xlsx", "csv"].includes(ext)) return "XLS";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "IMG";
  return "FILE";
};

const formatGroupKey = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DocSkeleton() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <div className="doc-item" key={i}>
          <Skeleton width={40} height={40} borderRadius={6} />
          <div className="docFlex">
            <Skeleton width={240} height={13} />
            <Skeleton width={160} height={11} style={{ marginTop: 6 }} />
          </div>
          <Skeleton width={80} height={30} borderRadius={6} />
        </div>
      ))}
    </>
  );
}

export default function DocumentsSection({ projectId }: DocumentsSectionProps) {
  const { documents, loading, downloadingId, handleDownload } = useDocuments(
    projectId,
    true,
  );

  const entries = documents ? Object.entries(documents) : [];
  const totalCount = entries.reduce(
    (sum, [, docs]) => sum + (docs as any[]).length,
    0,
  );

  return (
    <div className="ds active">
      <div className="dsHeader">
        <div className="dsHeaderTitle">Documents &amp; Files</div>
        {!loading && totalCount > 0 && (
          <span className="doc-meta">{totalCount} files</span>
        )}
      </div>

      {loading ? (
        <DocSkeleton />
      ) : totalCount === 0 ? (
        <EmptyState
          icon={<NoDocuments />}
          title="No Documents Yet"
          subtitle="Project documents will appear here once they are uploaded and published."
        />
      ) : (
        entries.map(([groupKey, docs]) => (
          <div
            className="doc-category"
            key={groupKey}
            style={{ marginBottom: 20 }}
          >
            <div className="doc-cat-title">{formatGroupKey(groupKey)}</div>
            {(docs as any[]).map((doc) => {
              const uploaderName = [
                doc.uploader?.firstName,
                doc.uploader?.lastName,
              ]
                .filter(Boolean)
                .join(" ");
              const date = doc.addOns?.date
                ? new Date(doc.addOns.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : new Date(doc.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
              const meta = [uploaderName, doc.uploader?.companyName, date]
                .filter(Boolean)
                .join(" · ");

              return (
                <div className="doc-item" key={doc.id}>
                  <div className="doc-icon docIconGreen">
                    {getIconLabel(doc.value?.fileName ?? "")}
                  </div>
                  <div className="docFlex">
                    <div className="doc-name">{doc.label}</div>
                    {meta && <div className="doc-meta">{meta}</div>}
                  </div>
                  <Button
                    className="doc-btn"
                    onClick={() => handleDownload(doc)}
                    disabled={downloadingId === doc.id}
                  >
                    {downloadingId === doc.id ? (
                      <>
                        <span className="btn-spinner" /> Downloading…
                      </>
                    ) : (
                      "Download"
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
